import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
    CartDescriptionException,
    CartExceptionType,
    PaymentMethods,
    PaymentMethodsType,
} from '@cargos/sprintpay-models';
import { map, of, Subject, switchMap, takeUntil } from 'rxjs';

import { CartBillService, PaymentCartService, PaymentMethodsService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import { PaymentMethodsHandlerService } from '../../shared/services/payment-methods-handler.service';
import { PaymentMethodSelectedComponent } from '../payment-method-selected/payment-method-selected.component';

@Component({
    standalone: true,
    selector: 'app-authenticated-payment-method',
    imports: [MatButtonModule, MatIconModule, PaymentMethodSelectedComponent],
    templateUrl: './authenticated-payment-method.component.html',
    styleUrls: ['./authenticated-payment-method.component.scss'],
})
export class AuthenticatedPaymentMethodComponent implements OnInit, OnDestroy {
    public paymentMethodSelected: PaymentMethodsType | null;
    public PaymentMethods = PaymentMethods;
    public showError: boolean = false;
    public msgError: string;
    public isSelectingPaymentMethod: boolean = false;
    private unsubscribe$: Subject<void> = new Subject<void>();

    /**
     * This variable do not define is the cart is above the threshold, is a configuration to get the payment method selected based on section
     */
    @Input() aboveThreshold: boolean = false;

    constructor(
        private cartBillService: CartBillService,
        private paymentMethodsService: PaymentMethodsService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService,
        private paymentCartService: PaymentCartService,
        private featureRestrictionsService: FeatureRestrictionsService
    ) {
        this.msgError = CartDescriptionException[CartExceptionType.PAYMENT_METHOD_INVALID];
    }

    ngOnInit(): void {
        this.subscribeGetDefaultPaymentMethodAndCart();
        this.submittedPaymentAndPaymentValid();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    submittedPaymentAndPaymentValid() {
        this.paymentCartService
            .getSubmitAccures()
            .pipe(
                switchMap((submitted: boolean) => {
                    if (submitted) {
                        return this.featureRestrictionsService.isPaymentMethodValid$().pipe(
                            map((valid: boolean) => {
                                return !valid;
                            })
                        );
                    }
                    return of(false);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: (invalidPaymentMethod: boolean) => {
                    this.showError = invalidPaymentMethod;
                },
            });
    }

    /**
     * @method subscribeGetDefaultPaymentMethodAndCart()
     * @description Preselect payment method
     */
    private subscribeGetDefaultPaymentMethodAndCart(): void {
        this.isSelectingPaymentMethod = true;

        this.paymentMethodsHandlerService
            .getPaymentMethodSelected$(this.aboveThreshold)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (selectedPayment) => {
                    this.isSelectingPaymentMethod = false;
                    if (
                        selectedPayment &&
                        this.paymentMethodsService.isPaymentMethodsSelectedOnCartAboveValid(selectedPayment) &&
                        this.cartBillService.isCartAboveThreshold()
                    ) {
                        this.paymentMethodSelected = selectedPayment?.method;
                        return;
                    } else if (!this.cartBillService.isCartAboveThreshold()) {
                        this.paymentMethodSelected = selectedPayment?.method || null;
                    } else {
                        this.paymentMethodSelected = null;
                    }
                },
                error: () => {
                    this.isSelectingPaymentMethod = false;
                },
            });
    }
}
